// @flow
import React, { Component, createRef } from 'react';
import InlineStyle from 'app/common/react/components/InlineStyle';
import StandardMasonry from 'app/common/react/components/Masonry9/StandardMasonry';
import { Masonry } from 'gestalt';
import { getMaxColumns, GRID_ITEM_WIDTH, MIN_COLUMNS } from 'app/common/lib/react/gridUtils';

// $FlowFixMe Flow v0.104.0 upgrade supression.
export type MasonryType = StandardMasonry<*> | Masonry<*>;

export type MasonryPaddingStyle = 'simplified' | 'dense';

type Props = {|
  columnWidth?: number,
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  comp: *,
  fetchBoundsOffset?: number,
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  flexible?: *,
  gutterWidth: number,
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  items: *,
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  loadItems?: *,
  masonryRef?: { current: ?MasonryType },
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  measurementStore?: *,
  paddingStyle?: MasonryPaddingStyle,
  scrollContainer?: () => HTMLElement,
  serverRender?: boolean,
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  virtualize?: *,
  widthOffset?: number,
  useMasonry9?: boolean,
  isGridCentered?: boolean,
  minCols?: number,
|};

const gridServerStyles = (gutterSize: number, pinCount: number, minColumns: number) => {
  const maxColumns = getMaxColumns(pinCount);
  const itemWidth = GRID_ITEM_WIDTH + gutterSize;

  let styles = `
.gridCentered {
  margin-left: auto;
  margin-right: auto;
}
.gridCentered .static,
.gridCentered .Masonry-Premount .Collection-Item {
  position: absolute;
  visibility: hidden;
}
.gridCentered .Masonry-Premount,
.gridCentered .Masonry-Premount .AutoSizer,
.gridCentered .Masonry-Premount .Collection {
  width: auto !important;
}
`;

  for (let i = minColumns; i < maxColumns + 1; i += 1) {
    const minWidth = i === minColumns ? 0 : i * itemWidth;
    styles += `
@media (min-width: ${minWidth}px) and (max-width: ${(i + 1) * itemWidth - 1}px) {
  .gridCentered {
    width: ${i * itemWidth}px;
  }
  .gridCentered .Masonry-Premount .Collection-Item:nth-child(-n+${i}),
  .gridCentered .static:nth-child(-n+${i}) {
    position: static !important;
    visibility: visible !important;
    float: left;
    display: block;
  }
}
`;
  }

  return styles;
};

export default class ExperimentalMasonry extends Component<Props> {
  static defaultProps = {
    gutterWidth: 14,
    isGridCentered: true,
    minCols: MIN_COLUMNS,
    paddingStyle: 'simplified',
    useMasonry9: false,
  };

  componentDidUpdate(prevProps: Props) {
    const { masonryRef } = this;
    if (this.props.widthOffset !== prevProps.widthOffset) {
      if (!this.props.useMasonry9 && masonryRef.current && masonryRef.current.handleResize) {
        // Gestalt Masonry requires manually calling handleResize()
        // $FlowFixMe: new errors found from flow 0.96 upgrade
        masonryRef.current.handleResize();
      }
    }
  }

  // $FlowFixMe Flow v0.104.0 upgrade supression.
  masonryRef = createRef<StandardMasonry<*> | Masonry<*>>();

  getGutters() {
    const { gutterWidth: gutterWidthProp, paddingStyle } = this.props;

    const simplifiedPadding = paddingStyle === 'simplified';
    const gutterWidth = simplifiedPadding ? 16 : gutterWidthProp;
    const paddingBottom = simplifiedPadding ? 16 : 6;

    return { gutterWidth, paddingBottom };
  }

  // $FlowFixMe Flow v0.104.0 upgrade supression.
  renderItem = (args: *) => {
    const { comp: Component } = this.props;
    const { gutterWidth, paddingBottom } = this.getGutters();

    return (
      <div
        style={{
          paddingBottom,
          paddingLeft: gutterWidth / 2,
          paddingRight: gutterWidth / 2,
        }}
      >
        <Component {...args} />
      </div>
    );
  };

  render() {
    const {
      columnWidth,
      fetchBoundsOffset,
      flexible,
      items,
      loadItems,
      masonryRef,
      measurementStore,
      scrollContainer,
      serverRender,
      useMasonry9,
      virtualize,
      isGridCentered,
      minCols,
    } = this.props;

    const { gutterWidth } = this.getGutters();

    let grid = null;

    if (useMasonry9) {
      grid = (
        <StandardMasonry
          columnWidth={columnWidth + gutterWidth}
          gutterWidth={0}
          comp={this.renderItem}
          minCols={minCols}
          ref={(masonry) => {
            this.masonryRef.current = masonry;

            if (masonryRef) {
              masonryRef.current = masonry;
            }
          }}
          items={items}
          flexible={flexible}
          loadItems={loadItems}
          measurementStore={measurementStore}
          scrollContainer={scrollContainer}
          virtualize={virtualize}
          fetchBoundsOffset={fetchBoundsOffset}
        />
      );
    } else {
      grid = (
        <Masonry
          columnWidth={columnWidth + gutterWidth}
          gutterWidth={0}
          comp={this.renderItem}
          minCols={minCols}
          ref={(masonry) => {
            this.masonryRef.current = masonry;

            if (masonryRef) {
              masonryRef.current = masonry;
            }
          }}
          items={items}
          flexible={flexible}
          loadItems={loadItems}
          measurementStore={measurementStore}
          scrollContainer={scrollContainer}
          virtualize={virtualize}
        />
      );
    }

    let className;
    if (isGridCentered) {
      className = 'gridCentered';
    }

    return (
      <div className={className}>
        {serverRender && (
          <InlineStyle
            unsafeCSS={gridServerStyles(gutterWidth, items.length, minCols || MIN_COLUMNS)}
          />
        )}
        <div>{grid}</div>
      </div>
    );
  }
}
